/**
 * SlideOver — Panel lateral derecho con animación slide-in.
 * Alternativa a StyledDialog para edición de entidades (Linear/Shopify pattern).
 *
 * Características:
 *   - Animación smooth de entrada/salida
 *   - ESC cierra
 *   - Click en backdrop cierra
 *   - Header sticky con título + acciones (guardar/cerrar)
 *   - Footer sticky con botones primarios
 *   - Scroll interno solo en el body
 *   - Trap de focus
 *
 * Props:
 *   open, onClose, title, subtitle?, icon?,
 *   width? (default 480), variant? ('default'|'wide')
 *   children, footer? (acciones custom — si null, usa el patrón estándar)
 */
import React, { useEffect } from 'react';
import { Box, Typography, IconButton, Drawer, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export default function SlideOver({
  zIndex,
  open,
  onClose,
  title,
  subtitle,
  icon,
  width,
  variant = 'default',
  children,
  footer,
  hideHeader = false,
}) {
  // ESC cierra
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const effectiveWidth = width || (variant === 'wide' ? 720 : 480);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      transitionDuration={250}
      // Permite subir el z-index cuando el SlideOver se abre encima de otro
      // Dialog (que usa z-index 1300 por default de MUI). Sin esta opción,
      // el Drawer queda por debajo del Dialog padre.
      sx={zIndex ? { zIndex } : undefined}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: effectiveWidth },
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fff',
        },
      }}
    >
      {/* Header sticky */}
      {!hideHeader && (
        <Box sx={{
          position: 'sticky', top: 0, zIndex: 2, bgcolor: '#fff',
          px: 2.5, py: 2, borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          {icon && (
            <Box sx={{
              width: 36, height: 36, borderRadius: 2,
              bgcolor: '#0d948815', color: '#0d9488',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>{icon}</Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }} noWrap>
              {title}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontSize: 11.5, color: '#64748b' }} noWrap>{subtitle}</Typography>
            )}
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      )}

      {/* Body scrolleable */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2.5, py: 2 }}>
        {children}
      </Box>

      {/* Footer sticky */}
      {footer && (
        <>
          <Divider />
          <Box sx={{
            position: 'sticky', bottom: 0, zIndex: 2, bgcolor: '#fff',
            px: { xs: 1.5, sm: 2.5 }, py: 1.5, display: 'flex', gap: 1,
            justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center',
            borderTop: '1px solid #e2e8f0',
          }}>
            {footer}
          </Box>
        </>
      )}
    </Drawer>
  );
}
